import { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { navigatePostLogin, resolvePostLoginDestination } from '../lib/redirect';
import { SignInPage } from '../pages/SignInPage';
import { SignUpPage } from '../pages/SignUpPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { PostLoginChooserPage } from '../pages/PostLoginChooserPage';
import { AuthCompletePage } from '../pages/AuthCompletePage';
import { LogoutPage } from '../pages/LogoutPage';
import { PartnerSignInPage } from '../pages/PartnerSignInPage';
import { PartnerForgotPasswordPage } from '../pages/PartnerForgotPasswordPage';
import { PartnerSetupPage } from '../pages/PartnerSetupPage';
import { PartnerResetPasswordPage } from '../pages/PartnerResetPasswordPage';
import { UserManagementPage } from '../pages/UserManagementPage';
import { InstanceManagementPage } from '../pages/InstanceManagementPage';
import { AccountPage } from '../pages/AccountPage';

const PUBLIC_PATHS = new Set([
  '/login',
  '/partner/login',
  '/partner/forgot-password',
  '/partner/setup',
  '/partner/reset-password',
  '/signup',
  '/forgot-password',
  '/auth/complete',
  '/logout',
]);

function LoginRoute(): JSX.Element {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) return;
    void resolvePostLoginDestination().then((destination) => {
      navigatePostLogin(destination, navigate);
    });
  }, [isLoggedIn, navigate]);

  if (isLoggedIn) {
    return <RouteSkeleton fullscreen />;
  }

  return <SignInPage />;
}

function SignUpRoute(): JSX.Element {
  const { isLoggedIn } = useAuth();
  if (isLoggedIn) {
    return <Navigate to="/welcome" replace />;
  }
  return <SignUpPage />;
}

function ForgotPasswordRoute(): JSX.Element {
  const { isLoggedIn } = useAuth();
  if (isLoggedIn) {
    return <Navigate to="/welcome" replace />;
  }
  return <ForgotPasswordPage />;
}

function PartnerForgotPasswordRoute(): JSX.Element {
  const { isLoggedIn } = useAuth();
  if (isLoggedIn) {
    return <Navigate to="/welcome" replace />;
  }
  return <PartnerForgotPasswordPage />;
}

function canonicalPathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '') || '/';
  }
  return pathname;
}

export function App(): JSX.Element {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  const pathname = canonicalPathname(location.pathname);

  if (pathname !== location.pathname) {
    return <Navigate to={`${pathname}${location.search}${location.hash}`} replace />;
  }

  const isPublic = PUBLIC_PATHS.has(pathname);

  if (!isLoggedIn && !isPublic) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return (
    <Suspense fallback={<RouteSkeleton fullscreen />}>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/partner/login" element={<PartnerSignInPage />} />
        <Route path="/partner/forgot-password" element={<PartnerForgotPasswordRoute />} />
        <Route path="/partner/setup" element={<PartnerSetupPage />} />
        <Route path="/partner/reset-password" element={<PartnerResetPasswordPage />} />
        <Route path="/signup" element={<SignUpRoute />} />
        <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/welcome" element={<ProtectedRoute><PostLoginChooserPage /></ProtectedRoute>} />
        <Route path="/auth/complete" element={<AuthCompletePage />} />
        <Route path="/admin/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />
        <Route path="/admin/instances" element={<ProtectedRoute><InstanceManagementPage /></ProtectedRoute>} />
        <Route path="/admin/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to={isLoggedIn ? '/welcome' : '/login'} replace />} />
      </Routes>
    </Suspense>
  );
}

function ProtectedRoute({ children }: { children: JSX.Element }): JSX.Element {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RouteSkeleton({ fullscreen = false }: { fullscreen?: boolean }): JSX.Element {
  return (
    <div className={`route-skeleton${fullscreen ? ' route-skeleton--fullscreen' : ''}`}>
      <div className="route-skeleton__pulse" />
      <p>Loading...</p>
    </div>
  );
}
